import { Component, forwardRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelect, MatSelectModule } from '@angular/material/select'
import { MatOptionModule } from '@angular/material/core'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'

export type SelectOption<T> = { value: T; labelKey: string; label?: never } | { value: T; label: string; labelKey?: never }

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [MatFormFieldModule, MatSelectModule, MatOptionModule, TranslatePipe, TranslateDirective],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent<T> implements ControlValueAccessor {
  @ViewChild(MatSelect) matSelect!: MatSelect

  @Input()
  labelKey!: string

  @Input()
  nullValueLabelKey = 'COMMON.USE_DEFAULT'

  @Input()
  allowNull = true

  @Input()
  options: SelectOption<T>[] = []

  @Input()
  beforeChange: ((value: T | null) => Promise<boolean>) | null = null

  currentValue: T | 0 = 0
  disabled = false

  private onChange: (value: T | null) => void = () => {}
  private onTouched: () => void = () => {}

  writeValue(value: T | null) {
    this.currentValue = value === null ? 0 : value
  }

  registerOnChange(fn: (value: T | null) => void) {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled
  }

  async onChangeValue(value: T | 0) {
    const newValue = value === 0 ? null : value
    if (this.beforeChange !== null) {
      const previousValue = this.currentValue
      this.matSelect.value = previousValue
      const approved = await this.beforeChange(newValue)
      if (!approved) {
        return
      }
    }
    this.currentValue = value
    this.onChange(newValue)
    this.onTouched()
  }
}
